import {
    OperationVariables,
    QueryLazyOptions,
    useReactiveVar,
} from '@apollo/client'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { stripDoubleSpacesTrimStart } from 'bpm/shared/utils/inputUtils'
import clsx from 'clsx'
import React, { ChangeEvent, FC, useCallback, useMemo, useState } from 'react'
import {
    addProductDialogState,
    setProductDialogState,
} from './addProductFormHandler'
import { AutoCompleteInput } from './AutoCompleteInput'
import { useAddProductFormStyles } from './formStyles'
import { ProductTypeAutocompleteOption } from './ProductTypeAutocompleteOption'

type Props = {
    callAutoSuggest: (
        options?: QueryLazyOptions<OperationVariables> | undefined
    ) => void
    autoSuggestData: any
    autoSuggestLoading: boolean
    disabled: boolean
}

const ExistingProductTypeAutoCompleteInput: FC<Props> = ({
    callAutoSuggest,
    autoSuggestData,
    autoSuggestLoading,
    disabled,
}) => {
    const classes = useAddProductFormStyles()

    const { errors, type, sectionNames, productType } = useReactiveVar(
        addProductDialogState
    )

    const [searchText, setSearchText] = useState('')

    const callAutoSuggestQuery = useCallback(
        (fetchString) => {
            if (!!type && !!sectionNames) {
                callAutoSuggest({
                    variables: {
                        input: {
                            listingType: type,
                            familyName: sectionNames,
                            productType: fetchString,
                            isProductTypeSearch: true,
                            limit: 50,
                        },
                    },
                })
            }
        },
        [type, searchText, callAutoSuggest, sectionNames]
    )

    const productTypeAutoSuggestOptions = useMemo(() => {
        return (
            autoSuggestData?.productListingDetailsAutoSuggest.productTypeDetails
                .reduce((acc: string[], curr: Record<string, string>) => {
                    acc.push(curr.formattedProductTypeName)
                    return acc
                }, [])
                .slice(0, 50)
                .sort((a: string, b: string) =>
                    a.localeCompare(b, undefined, { sensitivity: 'base' })
                ) ?? []
        )
    }, [autoSuggestData])

    const handleChange = useCallback(
        (_, value) => {
            const delimitedOption = value?.split('%|%')
            const tempValue =
                delimitedOption && delimitedOption?.length > 0
                    ? delimitedOption[0]
                    : ''
            setSearchText(tempValue)
            setProductDialogState({
                productType: tempValue,
                errors: { ...errors, productType: '' },
            })
        },
        [setProductDialogState, setSearchText, errors]
    )

    const handleInputChange = useCallback(
        (e: ChangeEvent<{}>, value: string) => {
            if (e?.type === 'clear') {
                setSearchText('')
                callAutoSuggestQuery('')
            }

            if (e?.type === 'change') {
                const delimitedOption = value?.split('%|%')
                const tempValue =
                    delimitedOption && delimitedOption?.length > 0
                        ? delimitedOption[0]
                        : ''

                setSearchText(tempValue ?? '')

                if (!!errors.productType) {
                    setProductDialogState({
                        errors: { ...errors, productType: '' },
                    })
                }

                callAutoSuggestQuery(stripDoubleSpacesTrimStart(tempValue))
            }
        },
        [
            setProductDialogState,
            errors,
            callAutoSuggestQuery,
            stripDoubleSpacesTrimStart,
        ]
    )

    const handleOpen = useCallback(() => {
        callAutoSuggestQuery(stripDoubleSpacesTrimStart(productType) ?? '')
    }, [callAutoSuggestQuery, productType, stripDoubleSpacesTrimStart])

    return (
        <GridItem
            className={clsx(classes.rowContainer, 'rowContainer')}
            data-testid="productType"
            xs={12}
        >
            <AutoCompleteInput
                clearOnBlur={true}
                name="productType"
                label="Product Type"
                disabled={disabled}
                valid={!errors.productType}
                handleOpen={handleOpen}
                handleChange={handleChange}
                handleInputChange={handleInputChange}
                selectedValue={productType}
                loading={autoSuggestLoading}
                items={productTypeAutoSuggestOptions ?? []}
                customOptions={(option: string) => (
                    <ProductTypeAutocompleteOption option={option} />
                )}
                getOptionLabel={(option) => option?.split('%|%')[0] ?? ''}
            />
        </GridItem>
    )
}
export default ExistingProductTypeAutoCompleteInput
